<template>
  <div class="date-range-picker-wrapper">
    <label class="drp-label">Date range:</label>
    <Datepicker
      ref="dp"
      :model-value="modelValue"
      range
      :multi-calendars="2"
      :partial-range="false"
      :enable-time-picker="true"
      :time-picker-inline="false"
      :close-on-auto-apply="false"
      :auto-apply="false"
      :transitions="false"
      class="grafana-datepicker"
      @update:model-value="onDateRangeChange"
      @internal-model-change="onInternalModelChange"
      @date-click="onDateClick"
    >
      <template #left-sidebar>
        <div class="grafana-sidebar">
          <button @click="applyQuickRange('last5m')">Last 5 minutes</button>
          <button @click="applyQuickRange('last15m')">Last 15 minutes</button>
          <button @click="applyQuickRange('last30m')">Last 30 minutes</button>
          <button @click="applyQuickRange('last1h')">Last 1 hour</button>
          <button @click="applyQuickRange('last3h')">Last 3 hours</button>
          <button @click="applyQuickRange('last6h')">Last 6 hours</button>
          <button @click="applyQuickRange('last12h')">Last 12 hours</button>
          <button @click="applyQuickRange('last24h')">Last 24 hours</button>
          <button @click="applyQuickRange('last2d')">Last 2 days</button>
          <button @click="applyQuickRange('last7d')">Last 7 days</button>
          <button @click="applyQuickRange('last30d')">Last 30 days</button>
          <button @click="applyQuickRange('last90d')">Last 90 days</button>
          <div class="grafana-sidebar-divider"></div>
          <button @click="applyQuickRange('today')">Today</button>
          <button @click="applyQuickRange('yesterday')">Yesterday</button>
          <button @click="applyQuickRange('thisWeek')">This week</button>
          <button @click="applyQuickRange('lastWeek')">Last week</button>
          <button @click="applyQuickRange('thisMonth')">This month</button>
          <button @click="applyQuickRange('lastMonth')">Last month</button>
          <button @click="applyQuickRange('thisYear')">This year</button>
          <button @click="applyQuickRange('lastYear')">Last year</button>
        </div>
      </template>
    </Datepicker>
  </div>
</template>

<script>
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

export default {
  name: 'DateRangePicker',
  components: { Datepicker },
  props: {
    modelValue: {
      type: Array,
      default: null
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      _lastClickDate: null,
      _lastClickAt: 0,
      _skipInternalUpdate: false,
      _internalModel: null,
    };
  },
  methods: {
    startOfDay(d) {
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
    },

    endOfDay(d) {
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
    },

    sameCalendarDay(a, b) {
      return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
    },

    isFullDayRange(from, to) {
      return from.getHours() === 0 && from.getMinutes() === 0 && from.getSeconds() === 0
        && to.getHours() === 23 && to.getMinutes() === 59 && to.getSeconds() === 59;
    },

    applyInternalRange(range) {
      this._skipInternalUpdate = true;
      this.$refs.dp?.updateInternalModelValue(range);
      this.$nextTick(() => { this._skipInternalUpdate = false; });
    },

    fmt(d) {
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} `
        + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },

    onDateClick(date) {
      const now = Date.now();
      const clicked = new Date(date);
      const isDoubleClick = this._lastClickDate
        && this.sameCalendarDay(this._lastClickDate, clicked)
        && now - this._lastClickAt < 400;

      if (isDoubleClick) {
        const current = this._internalModel?.length === 2
          ? this._internalModel
          : this.modelValue;
        let from;
        let to;

        if (current?.length === 2) {
          from = new Date(current[0]);
          to = new Date(current[1]);
          const hitsStart = this.sameCalendarDay(from, clicked);
          const hitsEnd = this.sameCalendarDay(to, clicked);

          if (hitsStart) from = this.startOfDay(clicked);
          if (hitsEnd) to = this.endOfDay(clicked);
          if (!hitsStart && !hitsEnd) {
            from = this.startOfDay(clicked);
            to = this.endOfDay(clicked);
          }
        } else {
          from = this.startOfDay(clicked);
          to = this.endOfDay(clicked);
        }

        this.applyInternalRange([from, to]);
      }

      this._lastClickDate = clicked;
      this._lastClickAt = now;
    },

    onInternalModelChange(value) {
      if (this._skipInternalUpdate) return;
      this._internalModel = value;
      if (!value || value.length !== 2) return;
      const [from, to] = value;
      if (this.sameCalendarDay(from, to) && !this.isFullDayRange(from, to)) {
        this.applyInternalRange([this.startOfDay(from), this.endOfDay(to)]);
      }
    },

    onDateRangeChange(value) {
      if (!value || value.length !== 2) return;
      let [from, to] = value;
      if (this.sameCalendarDay(from, to) && !this.isFullDayRange(from, to)) {
        from = this.startOfDay(from);
        to = this.endOfDay(to);
        value = [from, to];
      }
      this.$emit('update:modelValue', value);
      this.$emit('change', { dateFrom: this.fmt(from), dateTo: this.fmt(to) });
    },

    applyQuickRange(range) {
      const getNow = () => {
        const nyStr = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
        return new Date(nyStr);
      };

      const now = getNow();
      const startOfDay = (d) => this.startOfDay(d);
      const endOfDay = (d) => this.endOfDay(d);

      let from, to = getNow();

      switch (range) {
        case 'last5m':   from = new Date(now - 5*60*1000); break;
        case 'last15m':  from = new Date(now - 15*60*1000); break;
        case 'last30m':  from = new Date(now - 30*60*1000); break;
        case 'last1h':   from = new Date(now - 60*60*1000); break;
        case 'last3h':   from = new Date(now - 3*60*60*1000); break;
        case 'last6h':   from = new Date(now - 6*60*60*1000); break;
        case 'last12h':  from = new Date(now - 12*60*60*1000); break;
        case 'last24h':  from = new Date(now - 24*60*60*1000); break;
        case 'last2d':   from = new Date(now - 2*24*60*60*1000); break;
        case 'last7d':   from = new Date(now - 7*24*60*60*1000); break;
        case 'last30d':  from = new Date(now - 30*24*60*60*1000); break;
        case 'last90d':  from = new Date(now - 90*24*60*60*1000); break;
        case 'today':
          from = startOfDay(now); to = endOfDay(now); break;
        case 'yesterday': {
          const y = new Date(now); y.setDate(y.getDate() - 1);
          from = startOfDay(y); to = endOfDay(y); break;
        }
        case 'thisWeek': {
          const d = now.getDay(), diff = (d === 0 ? -6 : 1) - d;
          from = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff));
          to = endOfDay(now); break;
        }
        case 'lastWeek': {
          const d = now.getDay(), diff = (d === 0 ? -6 : 1) - d;
          const monThis = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff);
          from = startOfDay(new Date(monThis - 7*24*60*60*1000));
          to   = endOfDay(new Date(monThis - 24*60*60*1000)); break;
        }
        case 'thisMonth':
          from = new Date(now.getFullYear(), now.getMonth(), 1);
          to   = endOfDay(now); break;
        case 'lastMonth':
          from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          to   = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59); break;
        case 'thisYear':
          from = new Date(now.getFullYear(), 0, 1);
          to   = endOfDay(now); break;
        case 'lastYear':
          from = new Date(now.getFullYear() - 1, 0, 1);
          to   = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59); break;
        default: return;
      }

      const newRange = [from, to];
      this.$emit('update:modelValue', newRange);
      this.$emit('change', { dateFrom: this.fmt(from), dateTo: this.fmt(to) });
      this.$refs.dp.closeMenu();
    }
  }
}
</script>

<style scoped>
.date-range-picker-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drp-label {
  font-size: 12px;
  font-weight: 500;
  margin: 0;
  white-space: nowrap;
}

.grafana-sidebar {
  display: flex;
  flex-direction: column;
  width: 150px;
  padding: 8px 6px;
  background: #f5f5fa;
}

.grafana-sidebar button {
  background: none;
  border: none;
  color: #000000;
  font-size: 12px;
  padding: 6px 10px;
  text-align: left;
  cursor: pointer;
  border-radius: 4px;
}

.grafana-sidebar button:hover {
  background: #2d2d32;
  color: #ffffff;
}

.grafana-sidebar-divider {
  height: 1px;
  background: #2d2d32;
  margin: 4px 0;
}

.grafana-datepicker {
  width: 300px !important;
  font-size: 11px;
}

.grafana-datepicker :deep(.dp__menu) {
  font-size: 11px !important;
}

.grafana-datepicker :deep(.dp__calendar) {
  font-size: 11px !important;
}

.grafana-datepicker :deep(.dp__cell_inner) {
  font-size: 11px !important;
}

.grafana-datepicker :deep(.dp__input) {
  font-size: 11px !important;
  height: 32px !important;
  padding: 4px 8px 4px 35px !important;
}
</style>
